import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SalvButton, SalvCard, SalvProgressCircle, SalvSearchField, Size } from '@salvapp/ui-components';
import { useCreateCaseNote, useQueryCaseNotes } from './caseNoteQueryActions';
import { useUpdateCaseStatus } from '../management/caseManagementQueryActions';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import Has from '../../auth/Has';
import { CAN_ADD_NOTE, CAN_MODIFY_CASE, CAN_READ_CASE, CAN_READ_NOTE } from '../../auth/permissions';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { formatDate } from '../../common/datetime/FormattedDateTime';
import { ContentPreviewer } from '../../common/ContentPreviewer';
import {
  InternalApiCase,
  InternalApiCaseStatus,
  InternalApiDraftReferenceType,
  InternalApiInvestigationNote,
} from '../../api-internal';
import './CaseNotes.scss';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { CasePendingStatusContext } from '../management/CasePendingStatusContext';
import { DeleteNoteDraftDialog, NoteDraftConflictDialog, useNoteDrafts } from '../../note-drafts';
import {
  CASE_NOTE_DRAFTS_QUERY_KEY,
  useDeleteCaseNoteDraft,
  useQueryCaseNoteDrafts,
} from './caseNoteDraftQueryActions';
import { CaseNoteEditor } from './CaseNoteEditor';
import { NoteDraftItem } from '../../alerts/management/notes/NoteDraftItem';

interface Props {
  caseData: InternalApiCase;
}

const CaseNotes = ({ caseData: { handle, assignedStatus } }: Props) => {
  const dispatch = useDispatch();
  const { pendingStatus, setPendingStatus, clearPendingStatus } = useContext(CasePendingStatusContext)!;

  const { data: notes, isPending, isError, error } = useQueryCaseNotes(handle);
  const createCaseNote = useCreateCaseNote(handle);
  const updateCaseStatus = useUpdateCaseStatus(handle);

  const { data: caseDrafts } = useQueryCaseNoteDrafts(handle);
  const deleteDraftMutation = useDeleteCaseNoteDraft({ handle });

  const {
    isDraftEnabled,
    visibleDrafts,
    loadedExistingDraft,
    draftToDelete,
    conflictDraft,
    isTakingOver,
    pendingChange,
    isDraftActiveElsewhere,
    handleDraftIdChange,
    handleEditDraft,
    handleDeleteDraft,
    handleTakeover,
    confirmDeleteDraft,
    setPendingChange,
    setDraftToDelete,
    setConflictDraft,
    resetDraftState,
  } = useNoteDrafts({
    referenceId: handle,
    referenceType: InternalApiDraftReferenceType.CASE,
    drafts: caseDrafts,
    deleteDraft: async (id) => {
      await deleteDraftMutation.mutateAsync({ id });
    },
    draftsQueryKey: CASE_NOTE_DRAFTS_QUERY_KEY,
  });

  const [searchQuery, setSearchQuery] = useState('');

  const filteredNotes = useMemo(() => {
    if (isPending || isError) {
      return [];
    }
    if (!searchQuery) {
      return notes;
    }
    return notes.filter((n) => n.content.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [notes, searchQuery, isPending, isError]);

  useEffect(() => {
    if (pendingStatus && !pendingChange) {
      setPendingChange({ newNote: '' });
    }
  }, [pendingStatus, pendingChange, setPendingChange]);

  const handleDraftNoteSubmit = useCallback(
    async (content: string, status?: InternalApiCaseStatus, draftId?: number) => {
      try {
        if (status) {
          await updateCaseStatus.mutateAsync({ code: status.code, note: content, draftId });
          dispatch(showFeedback({ message: 'Status updated', success: true }));
        } else {
          await createCaseNote.mutateAsync({ content, draftId });
          dispatch(showFeedback({ message: 'Note added', success: true }));
        }
        setPendingChange(undefined);
        resetDraftState();
        clearPendingStatus();
      } catch {
        dispatch(showFeedback({ message: status ? 'Failed to update status' : 'Failed to add note', success: false }));
        throw new Error('Submit failed'); // re-throw so CaseNoteEditor can resume auto-save
      }
    },
    [updateCaseStatus, createCaseNote, dispatch, setPendingChange, resetDraftState, clearPendingStatus]
  );

  const handleDraftNoteCancel = useCallback(() => {
    setPendingChange(undefined);
    resetDraftState();
    clearPendingStatus();
  }, [setPendingChange, resetDraftState, clearPendingStatus]);

  const handlePendingStatusChange = useCallback(
    (status: InternalApiCaseStatus | undefined) => {
      if (status) {
        setPendingStatus(status);
      } else {
        clearPendingStatus();
      }
    },
    [setPendingStatus, clearPendingStatus]
  );

  const showEditor = pendingChange;

  return (
    <Has allPermissions={[CAN_READ_CASE, CAN_READ_NOTE]}>
      <SalvCard className='investigation-notes'>
        <SalvCard.Header
          actions={
            <>
              <SalvSearchField
                ariaLabel='Search notes'
                size={Size.SMALL}
                placeholder='Search submitted notes...'
                onChange={setSearchQuery}
                value={searchQuery}
              />
              {!pendingChange && (
                <Has allPermissions={[CAN_MODIFY_CASE, CAN_ADD_NOTE]}>
                  <SalvButton
                    label='Add note'
                    size={Size.SMALL}
                    variant='secondary'
                    icon={faPenToSquare}
                    onClick={() => setPendingChange({ newNote: '' })}
                  />
                </Has>
              )}
            </>
          }
        >
          Notes
        </SalvCard.Header>
        <SalvCard.Body className={'p-a-0'}>
          {showEditor && (
            <CaseNoteEditor
              handle={handle}
              existingDraft={loadedExistingDraft}
              assignedStatusCode={assignedStatus.code}
              pendingStatus={pendingStatus}
              isDraftEnabled={isDraftEnabled}
              onSubmit={handleDraftNoteSubmit}
              onCancel={handleDraftNoteCancel}
              onDraftIdChange={handleDraftIdChange}
              onPendingStatusChange={handlePendingStatusChange}
            />
          )}
          {isDraftEnabled &&
            visibleDrafts &&
            visibleDrafts.length > 0 &&
            visibleDrafts.map((draft) => (
              <NoteDraftItem
                key={`draft-${draft.id}`}
                draft={draft}
                isActiveElsewhere={isDraftActiveElsewhere(draft.id)}
                onEdit={handleEditDraft}
                onDelete={handleDeleteDraft}
              />
            ))}
          {isError ? (
            <DataLoadingErrorFallback message='Could not load Case Notes' error={error} />
          ) : isPending ? (
            <SalvProgressCircle ariaLabel='Loading Notes' />
          ) : notes.length === 0 && (!visibleDrafts || visibleDrafts.length === 0) && !showEditor ? (
            <div className='text-s p-a-16'>No notes added to this case</div>
          ) : filteredNotes.length === 0 && notes.length > 0 ? (
            <div className='text-s p-a-16'>No notes matching your search</div>
          ) : (
            filteredNotes.map((note: InternalApiInvestigationNote) => (
              <div key={`${note.createdAt.toISOString()}-${note.createdBy.name}`} className='case-note p-x-24 p-y-16'>
                <p className='m-0'>
                  <b>
                    {`${note.createdBy.name}, `}
                    {formatDate(note.createdAt, true)}
                  </b>
                </p>
                <ContentPreviewer previewLinesAmount={3}>{note.content}</ContentPreviewer>
              </div>
            ))
          )}
        </SalvCard.Body>
      </SalvCard>
      {draftToDelete && (
        <DeleteNoteDraftDialog
          draft={draftToDelete}
          show={!!draftToDelete}
          onClose={() => setDraftToDelete(null)}
          onConfirm={confirmDeleteDraft}
        />
      )}
      <NoteDraftConflictDialog
        isOpen={conflictDraft !== null}
        onTakeover={handleTakeover}
        onCancel={() => setConflictDraft(null)}
        isTakingOver={isTakingOver}
      />
    </Has>
  );
};

export default CaseNotes;
